/**
 * Firestore — users collection
 */
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  runTransaction,
} from "firebase/firestore";
import app from "../client";
import { getIdToken } from "../auth";
import type { UserProfile, SubscriptionPlan } from "@/shared/types";
import { planToTier } from "@/shared/types";

const db = getFirestore(app);

// ── Google Sheets signup webhook ──────────────────────────────────────────────
const SIGNUP_SHEET_URL =
  "https://script.google.com/macros/s/AKfycbx5p5KPlLLxBsjBqisCIwmVGLP4i1Gvm4dpAfJr4yoPpGFaXJJFgryAa0sGd36FzYPmPw/exec";

async function logNewUserToSheet(name: string, email: string): Promise<void> {
  try {
    const params = new URLSearchParams({
      Name:   name,
      Email:  email,
      Joined: new Date().toLocaleDateString("en-US", {
        month: "short", day: "numeric", year: "numeric",
      }),
    });
    await fetch(SIGNUP_SHEET_URL, { method: "POST", mode: "no-cors", body: params });
  } catch {
    // Best-effort — never block account creation
  }
}

// ── MailerLite signup webhook ────────────────────────────────────────────────
// New accounts (email/password and Google alike) are pushed to MailerLite the
// moment their profile is created — the same trigger as the Sheets log above.
// The API key never reaches the browser: the client only sends a Firebase ID
// token and /api/mailerlite/signup reads the address from the token claims.
async function syncNewUserToMailerLite(): Promise<void> {
  try {
    // Cached token only — do NOT force a refresh here. Forcing one straight
    // after signInWithPopup races the OAuth credential exchange and can reject,
    // which silently killed the whole sync for Google signups. The server reads
    // the display name from the Firestore profile doc, which is written after
    // updateProfile, so the name is correct without a refresh.
    const token = await getIdToken();
    if (!token) {
      console.error("MailerLite signup sync: no signed-in user, skipping");
      return;
    }
    const res = await fetch("/api/mailerlite/signup", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      // Survives a page unload / hard navigation. Google signups set
      // _isFreshSignIn, which can trigger the domain-redirect branch in
      // AuthContext and navigate away while this fire-and-forget POST is
      // still open; without keepalive the browser would cancel it.
      keepalive: true,
    });
    if (!res.ok) {
      const body = await res.text().catch(() => "");
      console.error("MailerLite signup sync failed:", res.status, body);
    }
  } catch (err) {
    // Best-effort — never block account creation, but always leave a trace.
    console.error("MailerLite signup sync threw:", err);
  }
}

export async function getUserProfile(userId: string): Promise<UserProfile | null> {
  const snap = await getDoc(doc(db, "users", userId));
  if (!snap.exists()) return null;
  return snap.data() as UserProfile;
}

/**
 * The profile a fresh signup should have.
 *
 * Built in one place so the signup page and the Auth listener cannot drift
 * apart on which fields they bother to set — that drift is what lost names.
 *
 * `overrides.displayName` exists because those two writers disagree about
 * timing: the listener fires in between `createUserWithEmailAndPassword` and
 * `updateProfile`, so reading `user.displayName` there can catch it while it is
 * still null. The signup page has already awaited the write, so it passes the
 * name it knows rather than asking the user object.
 */
export function buildInitialProfile(
  user: {
    uid: string;
    email?: string | null;
    displayName?: string | null;
    photoURL?: string | null;
  },
  overrides: { displayName?: string } = {},
): Omit<UserProfile, "id" | "createdAt" | "updatedAt"> {
  const displayName = overrides.displayName ?? user.displayName ?? "";
  return {
    email: user.email ?? "",
    uid: user.uid,
    systemRole: "customer",
    subscription: { plan: "free", tier: "free", status: "active" },
    // Capture the hostname at registration time for subdomain routing.
    // Defaults to "www.scmsensei.ai" if running server-side or unknown.
    signup_domain:
      typeof window !== "undefined" ? window.location.hostname : "www.scmsensei.ai",
    ...(displayName ? { displayName } : {}),
    ...(user.photoURL ? { photoURL: user.photoURL } : {}),
  };
}

export async function createUserProfile(
  userId: string,
  data: Omit<UserProfile, "id" | "createdAt" | "updatedAt">
): Promise<boolean> {
  const created = await runTransaction(db, async (tx) => {
    const ref = doc(db, "users", userId);
    const existing = await tx.get(ref);
    if (existing.exists()) {
      // Fill only the blanks; never overwrite what is already there. The
      // signup page and the Auth listener race by a fraction of a second, and
      // "it exists, so leave it entirely alone" is precisely what turned that
      // race into a full name that never reached the database. An edited name
      // or a plan someone paid for still cannot be clobbered, because those
      // fields are not blank.
      const current = existing.data() as Record<string, unknown>;
      const fill: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(data)) {
        if (value === undefined || value === null || value === "") continue;
        const present = current[key];
        if (present === undefined || present === null || present === "") {
          fill[key] = value;
        }
      }
      if (Object.keys(fill).length > 0) {
        tx.update(ref, { ...fill, updatedAt: serverTimestamp() });
      }
      return false;
    }
    tx.set(ref, {
      ...data,
      id: userId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return true;
  });
  if (created) {
    void logNewUserToSheet(data.displayName ?? "", data.email ?? "");
    void syncNewUserToMailerLite();
  }
  return created;
}

export async function updateUserProfile(
  userId: string,
  data: Partial<Omit<UserProfile, "id" | "createdAt">>
): Promise<void> {
  await updateDoc(doc(db, "users", userId), {
    ...data,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Persist that a free user has consumed their single RFP Pack allowance.
 * Life-long marker: deleting the pack does not restore the allowance.
 */
export async function setFreePackUsed(userId: string): Promise<void> {
  await updateUserProfile(userId, { freePackUsed: true });
}

/**
 * Persist that a free user has consumed their single Contract Review
 * allowance. Life-long marker: deleting the review does not restore it.
 */
export async function setFreeContractUsed(userId: string): Promise<void> {
  await updateUserProfile(userId, { freeContractUsed: true });
}

// ─── Subscription helpers ─────────────────────────────────────────────────────

/**
 * Write subscription state to Firestore after a successful Stripe checkout.
 * Mirrors reactjs/src/lib/userProfile.ts → updateSubscriptionFromStripe
 */
export async function updateSubscriptionFromStripe(
  userId: string,
  data: {
    plan: SubscriptionPlan;
    status: string;
    stripeCustomerId: string;
    stripeSubscriptionId: string;
    priceId: string;
    currentPeriodEnd: number; // Unix timestamp seconds
  }
): Promise<void> {
  await updateDoc(doc(db, "users", userId), {
    subscription: {
      plan: data.plan,
      tier: planToTier(data.plan),
      status: data.status,
      startDate: new Date().toISOString(),
      endDate: new Date(data.currentPeriodEnd * 1000).toISOString(),
      currentPeriodEnd: new Date(data.currentPeriodEnd * 1000).toISOString(),
      stripeCustomerId: data.stripeCustomerId,
      stripeSubscriptionId: data.stripeSubscriptionId,
      priceId: data.priceId,
    },
    updatedAt: serverTimestamp(),
  });
}

/**
 * Write subscription state to Firestore after a successful PayPal subscription.
 * Mirrors reactjs/src/lib/userProfile.ts → updateSubscriptionFromPayPal
 */
export async function updateSubscriptionFromPayPal(
  userId: string,
  data: {
    plan: "pro_monthly" | "pro_annual";
    paypalSubscriptionId: string;
  }
): Promise<void> {
  const durationMs =
    data.plan === "pro_annual"
      ? 365 * 24 * 60 * 60 * 1000
      : 30 * 24 * 60 * 60 * 1000;

  const endDate = new Date(Date.now() + durationMs).toISOString();

  await updateDoc(doc(db, "users", userId), {
    subscription: {
      plan: data.plan,
      tier: planToTier(data.plan),
      status: "active",
      startDate: new Date().toISOString(),
      endDate,
      currentPeriodEnd: endDate,
      paypalSubscriptionId: data.paypalSubscriptionId,
    },
    updatedAt: serverTimestamp(),
  });
}

/**
 * Mark a subscription as cancelled at period end.
 * Called immediately after a successful cancel API call so the UI
 * reflects the change without waiting for a webhook.
 */
export async function updateSubscriptionCancelAtPeriodEnd(
  userId: string,
  cancelAtPeriodEnd: boolean
): Promise<void> {
  await updateDoc(doc(db, "users", userId), {
    "subscription.cancelAtPeriodEnd": cancelAtPeriodEnd,
    "subscription.status": cancelAtPeriodEnd ? "cancelled" : "active",
    updatedAt: serverTimestamp(),
  });
}

// ─── Soft delete ─────────────────────────────────────────────────────────────

/**
 * Soft-delete a user account.
 * Sets `deleteRequestedAt` to the current ISO timestamp. The account remains
 * in Firestore — the team processes the deletion manually.
 */
export async function softDeleteUser(userId: string): Promise<void> {
  await updateDoc(doc(db, "users", userId), {
    deleteRequestedAt: new Date().toISOString(),
    updatedAt: serverTimestamp(),
  });
}

// ─── Admin helpers ────────────────────────────────────────────────────────────

import { collection, getDocs } from "firebase/firestore";

/** Fetch every user document — superadmin only. */
export async function getAllUsers(): Promise<UserProfile[]> {
  const snap = await getDocs(collection(db, "users"));
  return snap.docs.map((d) => d.data() as UserProfile);
}

/** Change a user's systemRole. */
export async function adminSetUserRole(
  targetUid: string,
  role: UserProfile["systemRole"]
): Promise<void> {
  await updateDoc(doc(db, "users", targetUid), {
    systemRole: role,
    updatedAt: new Date().toISOString(),
  });
}

/** Toggle the isDemo flag on a user. */
export async function adminSetDemoStatus(
  targetUid: string,
  isDemo: boolean
): Promise<void> {
  await updateDoc(doc(db, "users", targetUid), {
    isDemo,
    updatedAt: new Date().toISOString(),
  });
}

/** Toggle the isStudent flag on a user. */
export async function adminSetStudentStatus(
  targetUid: string,
  isStudent: boolean
): Promise<void> {
  await updateDoc(doc(db, "users", targetUid), {
    isStudent,
    updatedAt: new Date().toISOString(),
  });
}

/**
 * Set a user's subscription plan and recalculate start/end dates.
 * Used by the plan <select> in the admin table.
 */
export async function adminSetUserPlan(
  targetUid: string,
  plan: SubscriptionPlan
): Promise<void> {
  const now = new Date();
  let endDate: string;
  if (plan === "pro_monthly") {
    endDate = new Date(now.getFullYear(), now.getMonth() + 1, now.getDate()).toISOString();
  } else if (plan === "pro_annual") {
    endDate = new Date(now.getFullYear() + 1, now.getMonth(), now.getDate()).toISOString();
  } else {
    endDate = new Date(now.getFullYear() + 1, now.getMonth(), now.getDate()).toISOString();
  }

  await updateDoc(doc(db, "users", targetUid), {
    "subscription.plan": plan,
    "subscription.tier": planToTier(plan),
    "subscription.status": "active",
    "subscription.startDate": now.toISOString(),
    "subscription.endDate": endDate,
    updatedAt: new Date().toISOString(),
  });
}

/**
 * Extend an existing subscription by 1 month or 1 year.
 * If the subscription is expired / free, the extension starts from now.
 * Requires the calling user to be superadmin — enforced server-side via
 * Firestore rules; we do a client-side sanity check here too.
 */
export async function adminExtendMembership(
  adminUid: string,
  targetUid: string,
  durationInDays: 30 | 365,
  plan: "pro_monthly" | "pro_annual" = "pro_monthly",
  reason = "admin_extension"
): Promise<{ previousEndDate: string; newEndDate: string }> {
  // Client-side admin check
  const adminSnap = await getDoc(doc(db, "users", adminUid));
  if (!adminSnap.exists() || adminSnap.data().systemRole !== "superadmin") {
    throw new Error("Unauthorized: only superadmins can extend memberships");
  }

  const targetSnap = await getDoc(doc(db, "users", targetUid));
  if (!targetSnap.exists()) throw new Error("Target user not found");

  const userData = targetSnap.data() as UserProfile;
  const currentEnd = new Date(userData.subscription.endDate ?? Date.now());
  const now = new Date();
  const base = currentEnd > now && userData.subscription.plan !== "free" ? currentEnd : now;

  const newEnd = new Date(base);
  if (durationInDays === 365) {
    newEnd.setFullYear(newEnd.getFullYear() + 1);
  } else {
    newEnd.setMonth(newEnd.getMonth() + 1);
  }

  await updateDoc(doc(db, "users", targetUid), {
    "subscription.plan": plan,
    "subscription.tier": planToTier(plan),
    "subscription.status": "active",
    "subscription.endDate": newEnd.toISOString(),
    updatedAt: new Date().toISOString(),
  });

  console.log(`Admin ${adminUid} extended membership for ${targetUid} (${reason})`);
  return { previousEndDate: currentEnd.toISOString(), newEndDate: newEnd.toISOString() };
}

/** Update a single subscription date field (startDate or endDate). */
export async function adminUpdateSubscriptionDate(
  targetUid: string,
  field: "startDate" | "endDate",
  isoDate: string
): Promise<void> {
  await updateDoc(doc(db, "users", targetUid), {
    [`subscription.${field}`]: new Date(isoDate).toISOString(),
    updatedAt: new Date().toISOString(),
  });
}
